/**
 * The sky itself, and the things in it: the gradient, stars, sun and moon
 * (T116, **D59**).
 *
 * 🎯 **haugur:** *"i would like both sun and moon to be light emitters in the
 * distand, dont need to be in any detail, just that when i look up i will know
 * where they are at."*
 *
 * ## 🔴 Two things here were rebuilt on 2026-08-11 because the obvious way is broken
 *
 * **1. The sky is a SHADER, not a background texture.** `scene.background` with
 * an equirectangular texture is **converted to a cube map once and cached**, and
 * the cache is only cleared when the texture is *disposed*:
 *
 * ```js
 * if ( _cache$1.has( texture ) ) { …reuse… }
 * else { renderTarget.fromEquirectangularTexture( renderer, texture ); _cache$1.set( … ) }
 * ```
 *
 * **So mutating the pixels and setting `needsUpdate` does nothing at all.** The
 * sky was frozen at whatever colour it held on the first frame — which is why it
 * looked like a perfectly good blue sky and never once responded to the weather
 * or the time. Two colour **uniforms** on a dome have no such cache.
 *
 * **2. Stars are QUADS, not `Points`.** `PointsNodeMaterial.setupVertex` reads:
 *
 * ```js
 * if ( builder.object.isPoints ) return super.setupVertex( builder );  // size ignored
 * else return this.setupVertexSprite( builder );                       // size applied
 * ```
 *
 * **A plain `Points` object skips the point-size code entirely**, so `size` and
 * `sizeAttenuation` do nothing and every star draws one pixel wide. ✅ **A quad
 * tangent to the dome always faces the camera**, because the camera is at the
 * dome's centre — so there is no billboarding to write and no exotic material to
 * depend on.
 *
 * ⚠️ **Both were found by reading the installed three r180 source, not by
 * guessing** — the T94 rule, and both would have been invisible to every test we
 * have.
 *
 * ## The sizes, chosen by looking
 *
 * The real sun and moon are 0.53° across — about four pixels here. haugur picked
 * **×4 for the sun** and **×8 for the moon** from a rendered comparison; the moon
 * is larger because **its phase carries information** about how much light the
 * night will have.
 */

import {
  AdditiveBlending,
  BackSide,
  DoubleSide,
  BufferAttribute,
  BufferGeometry,
  Color,
  DataTexture,
  Group,
  LinearFilter,
  Mesh,
  Quaternion,
  RepeatWrapping,
  SRGBColorSpace,
  SphereGeometry,
  Sprite,
  Vector3,
  type Scene,
} from 'three';
import { MeshBasicNodeMaterial, SpriteNodeMaterial } from 'three/webgpu';
import {
  asin,
  atan2,
  float,
  mix,
  positionLocal,
  /* ⚠️ Aliased: this module already has a plain-number `smoothstep` helper for
     the sun and moon fades, and the two are not interchangeable. */
  smoothstep as smoothstepNode,
  texture,
  uniform,
  vec2,
} from 'three/tsl';
import {
  AURORA_BEARING_SPAN,
  AURORA_ELEVATION_SPAN,
  PHOTOGRAPH_VALUE_SCALE,
  WORLD_LATITUDE,
  auroraLook,
  paintAuroraTexture,
} from '@lofi/shared';
import {
  DISC_TEXTURE_SIZE,
  STAR_TEXTURE_SIZE,
  starField,
  writeDiscTexture,
  writeMoonTexture,
  writeStarTexture,
} from './skytextures.ts';

/**
 * How far away the sun, moon and stars are drawn, in metres.
 *
 * ⚠️ **Inside the camera's far plane (2,000 m) and outside the render distance
 * (768 m at most).** Being inside the far plane means ordinary depth testing
 * hides them behind terrain with no special case: stand under a cliff and the
 * cliff covers the sun, which is what a cliff does.
 */
const SKY_RADIUS = 1500;

/** The dome sits behind everything else, and never writes depth. */
const DOME_RADIUS = 1900;

/** The real angular diameter of both bodies, degrees. */
const TRUE_DIAMETER = 0.53;
const SUN_EXAGGERATION = 4;
const MOON_EXAGGERATION = 8;

/**
 * How many stars.
 *
 * ⚠️ **1,400 → 2,800 when the field became a full sphere** (half are below the
 * horizon at any moment), **then down to 1,800 because it read as too busy.**
 * 🎯 haugur: *"and just maybe a little to many."* About **900 visible** at once.
 *
 * ⚠️ **The count is only half of why a sky looks crowded** — the other half is
 * every star being the same brightness. That is fixed in `starField`, which now
 * biases hard toward the faint.
 */
const STAR_COUNT = 1800;

/**
 * How big a star is drawn, in degrees — **faintest to brightest**.
 *
 * ⚠️ **Was a flat 0.28°, and that was about 2½ times too big.** Measured against
 * the moon in haugur's screenshot: the moon is ×8, or 4.24°, and the stars came
 * out at roughly a sixth of its width when they should have been a fifteenth.
 * **A hard-edged square also reads larger than a soft dot of the same width**, so
 * the two faults compounded.
 *
 * 🔴 **Size varies with brightness now, and that matters more than the absolute
 * value.** A real sky is a few obvious stars among many you can barely see; a
 * field of identical dots reads as noise however small they are.
 *
 * ⚠️ **A fixed ANGULAR size, not a fixed pixel size** — so the sky looks the same
 * on every display, which the `Points` path could never have given.
 */
const STAR_DIAMETER_FAINT = 0.08;
const STAR_DIAMETER_BRIGHT = 0.2;

/** How finely the moon's texture is rebuilt, in phases of a lunation. */
const MOON_TEXTURE_STEPS = 64;

/**
 * How much of the sun you can still make out through complete cloud.
 *
 * 🎯 **haugur, 2026-08-11:** *"even when its cloude i want to be at least make
 * out where it is, maybe not as visible as on a clear day but at least be able to
 * see it."* ✅ **And he is right about the real thing** — Icelandic overcast is
 * usually thin enough to show a bright patch where the sun is.
 *
 * ⚠️ **Stars get no such floor.** You genuinely cannot see a star through cloud,
 * and pretending otherwise would make the weather meaningless at night.
 */
const SUN_THROUGH_CLOUD = 0.3;
/** The moon is fainter than the sun, so less of it survives the cloud. */
const MOON_THROUGH_CLOUD = 0.15;

function worldSize(degrees: number, radius: number): number {
  return 2 * radius * Math.tan((degrees * Math.PI) / 360);
}

function discTexture(build: (data: Uint8Array) => void, size = DISC_TEXTURE_SIZE): DataTexture {
  const data = new Uint8Array(size * size * 4);
  build(data);
  const texture = new DataTexture(data, size, size);
  texture.colorSpace = SRGBColorSpace;
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

/**
 * 🌌 **The aurora's texture** (T114). Wide and short, because the curtain is a
 * band: almost all the detail that matters is horizontal.
 */
/**
 * 🔴 **512 × 128, and it was 1024 × 256 until haugur reported 3 FPS.**
 *
 * The painter runs on the CPU, and the larger texture is **262,144 pixels of
 * `Math.pow` per repaint** — tens of milliseconds, on the main thread, eight
 * times a second. **That was the frame rate.** Quartering the pixels and halving
 * the rate is a sixteenfold cut in the work.
 *
 * ⚠️ **The resolution costs nothing visible here** — the curtain is soft-edged by
 * nature and the texture is stretched across 200° of sky, so the sampling was
 * never the limit on how it looks.
 */
const AURORA_TEXTURE_WIDTH = 512;
const AURORA_TEXTURE_HEIGHT = 128;

/** What the sky needs to know each frame. */
export interface SkyObjectsState {
  readonly sunDirection: { readonly x: number; readonly y: number; readonly z: number };
  readonly sunElevation: number;
  readonly moonDirection: { readonly x: number; readonly y: number; readonly z: number };
  readonly moonElevation: number;
  /** 0…1 through the lunation — **which limb is lit**, not merely how much. */
  readonly moonPhase: number;
  /** 0 broad day … 1 properly dark. */
  readonly night: number;
  readonly cloud: number;
  readonly fog: number;
  /** For the star wheel: the world's day count, with its fraction. */
  readonly days: number;
  /** 0…1 strength of the warm glow around the sun (2026-08-11). */
  readonly sunsetGlow: number;
  /** Its colour — measured from haugur's Urðarstígur frames. */
  readonly glowColour: string;
  /**
   * 🌌 **The planetary geomagnetic index, 0–9** (T114, **D61**), for the real
   * year this world drew. The sky works out the rest from this, the sun's
   * elevation and the cloud — it does not need telling how bright to be.
   */
  readonly kp: number;
  /**
   * Real seconds since the client started. 🔴 **Real, not game.** A game hour is
   * 50 real seconds, so a curtain drifting on game time would strobe.
   */
  readonly realSeconds: number;
}

export class SkyObjects {
  private readonly group = new Group();
  private readonly dome: Mesh;
  private readonly zenith = uniform(new Color('#9ca9b3'));
  private readonly horizon = uniform(new Color('#a9b2ba'));
  /* 🌇 The sunset, which is DIRECTIONAL — see the colour node below. */
  private readonly glowColour = uniform(new Color('#df7679'));
  private readonly glowStrength = uniform(0);
  private readonly sunTowards = uniform(new Vector3(0, 1, 0));
  private readonly stars: Mesh;
  private readonly starMaterial: MeshBasicNodeMaterial;
  private readonly sun: Sprite;
  private readonly sunMaterial: SpriteNodeMaterial;
  private readonly moon: Sprite;
  private readonly moonMaterial: SpriteNodeMaterial;
  private readonly moonTexture: DataTexture;
  private readonly pole: Vector3;
  private readonly spin = new Quaternion();
  private moonStep = -1;

  /* 🌌 The aurora (T114). */
  private readonly aurora: Mesh;
  private readonly auroraMaterial: MeshBasicNodeMaterial;
  private readonly auroraTexture: DataTexture;
  private readonly auroraOpacity = uniform(0);
  /** How far the curtain has slid, in texture widths. See `updateAurora`. */
  private readonly auroraDrift = uniform(0);
  private auroraLastSeconds = 0;
  /** The Kp the current texture was painted for; `null` means never painted. */
  private auroraPaintedKp: number | null = null;

  constructor(scene: Scene) {
    /* ⚠️ Not frustum-culled: everything is repositioned relative to the camera
       every frame, so three's cached bounding spheres are always a frame stale
       and would cull the sun at the worst moment. */
    this.group.frustumCulled = false;

    // ── the sky itself ─────────────────────────────────────────────────────
    const domeMaterial = new MeshBasicNodeMaterial();
    /* Linear in the elevation angle, and that is physically right rather than
       merely simple: Design §12.6 sampled the photographs by elevation band.
       ⚠️ `.max(0)` holds the whole lower hemisphere at the horizon colour — the
       ground covers most of it, and what shows through past the loaded chunks
       must match the fog exactly or the edge of the world reads as a seam. */
    /**
     * 🔴 **The sky is two stops by ALTITUDE plus a glow by DIRECTION**, and the
     * second half was added on 2026-08-11 after haugur photographed the same
     * sunset looking toward it and away from it.
     *
     * **Toward the sun: vivid pink. Away: pale grey-lilac. Same minute.** So a
     * sunset cannot be a property of how high the sun is — smeared evenly round
     * the horizon it does not read as a sunset at all, it cancels to grey,
     * because pale blue and salmon are near enough complementary.
     *
     * `toSun` cubed concentrates the glow around the sun rather than washing
     * half the sky; the `1 − y` term keeps it low, where the light is coming
     * through the most air.
     */
    const direction = positionLocal.normalize();
    const base = mix(this.horizon, this.zenith, direction.y.max(0));
    const towardSun = direction.dot(this.sunTowards).max(0).pow(3);
    const lowDown = direction.y.max(0).oneMinus().pow(1.5);
    domeMaterial.colorNode = mix(
      base,
      this.glowColour,
      towardSun.mul(lowDown).mul(this.glowStrength).min(1),
    );
    domeMaterial.side = BackSide;
    domeMaterial.depthWrite = false;
    domeMaterial.depthTest = false;
    domeMaterial.fog = false;
    domeMaterial.toneMapped = false;

    this.dome = new Mesh(new SphereGeometry(DOME_RADIUS, 32, 16), domeMaterial);
    this.dome.frustumCulled = false;
    /* Drawn before everything, and writing no depth, so terrain covers it. */
    this.dome.renderOrder = -1;

    // ── stars ──────────────────────────────────────────────────────────────
    this.stars = new Mesh(starGeometry(), (this.starMaterial = new MeshBasicNodeMaterial()));
    this.starMaterial.vertexColors = true;
    this.starMaterial.transparent = true;
    this.starMaterial.depthWrite = false;
    /* ⚠️ `fog: false` on everything up here. Scene fog fades distant things
       toward the horizon colour, which is right for terrain and wrong for the
       sky — the sky IS the colour it would fade into. Cloud hides these instead
       (D59), which is a different mechanism entirely. */
    this.starMaterial.fog = false;
    /* Additive: a star is light arriving, not a white sticker. */
    this.starMaterial.blending = AdditiveBlending;
    /* 🔴 A soft round dot, or every star is a square. Additive blending adds the
       colour regardless of alpha, so the falloff is in the RGB as well. */
    this.starMaterial.map = discTexture((data) => writeStarTexture(data), STAR_TEXTURE_SIZE);
    /* ⚠️ **`DoubleSide`, and it is not laziness.** A star quad is built from a
       tangent basis whose handedness depends on which helper vector was used to
       avoid the degenerate case at the poles — so the winding, and therefore
       which face is "front", flips for some stars. A quad has no meaningful back;
       making both sides draw removes a whole class of "some stars are missing"
       that would be near-impossible to see the cause of. */
    this.starMaterial.side = DoubleSide;
    this.stars.frustumCulled = false;

    // ── the sun and the moon ───────────────────────────────────────────────
    const sunTexture = discTexture((data) => writeDiscTexture(data));
    this.moonTexture = discTexture((data) => writeMoonTexture(data, 0.5));

    this.sunMaterial = new SpriteNodeMaterial();
    this.sunMaterial.map = sunTexture;
    this.sunMaterial.transparent = true;
    this.sunMaterial.depthWrite = false;
    this.sunMaterial.fog = false;
    this.sunMaterial.blending = AdditiveBlending;
    this.sun = new Sprite(this.sunMaterial);
    this.sun.frustumCulled = false;
    const sunSize = worldSize(TRUE_DIAMETER * SUN_EXAGGERATION, SKY_RADIUS);
    this.sun.scale.set(sunSize, sunSize, 1);

    this.moonMaterial = new SpriteNodeMaterial();
    this.moonMaterial.map = this.moonTexture;
    this.moonMaterial.transparent = true;
    this.moonMaterial.depthWrite = false;
    this.moonMaterial.fog = false;
    /* ⚠️ NOT additive, unlike the sun. The moon is a lit rock and its dark limb
       must be genuinely absent — additive cannot subtract, so a crescent would
       come out as a full disc with one bright edge. */
    this.moon = new Sprite(this.moonMaterial);
    this.moon.frustumCulled = false;
    const moonSize = worldSize(TRUE_DIAMETER * MOON_EXAGGERATION, SKY_RADIUS);
    this.moon.scale.set(moonSize, moonSize, 1);

    // ── norðurljós ─────────────────────────────────────────────────────────
    /**
     * 🔴 **The lookup is computed from the DIRECTION, not from the geometry's
     * UVs, and that is a deliberate choice about what can go wrong unseen.**
     *
     * A sphere's built-in UVs would work, but only if three's `v` convention and
     * `DataTexture`'s `flipY` default both happen to line up with the painter's
     * *"row 0 is the zenith"*. **That is two conventions I cannot check, on a
     * feature nobody can see from here, where being wrong means an upside-down
     * aurora that still looks like a plausible aurora.**
     *
     * Deriving `u` and `v` from the world direction depends on neither. It says
     * exactly what the painter says: **`u` is the bearing either side of north,
     * `v` runs from the horizon up to the zenith.**
     *
     * ⚠️ North is **−z** (`world.ts`), so the bearing is `atan2(x, −z)`.
     */
    this.auroraTexture = new DataTexture(
      new Uint8Array(AURORA_TEXTURE_WIDTH * AURORA_TEXTURE_HEIGHT * 4),
      AURORA_TEXTURE_WIDTH,
      AURORA_TEXTURE_HEIGHT,
    );
    this.auroraTexture.colorSpace = SRGBColorSpace;
    this.auroraTexture.minFilter = LinearFilter;
    this.auroraTexture.magFilter = LinearFilter;
    /* ⚠️ Repeating, so the drift above can slide past the edge and come back.
       The visible sector is bounded by the mask, not by the wrapping. */
    this.auroraTexture.wrapS = RepeatWrapping;
    this.auroraTexture.needsUpdate = true;

    this.auroraMaterial = new MeshBasicNodeMaterial();
    {
      const direction = positionLocal.normalize();
      const RAD_TO_DEG = 180 / Math.PI;

      /**
       * 🔴 **The ribbon's own coordinates, not the sky's** — option C, 2026-08-12.
       *
       * | | |
       * |---|---|
       * | **`along`** | where you are **on** the great circle: **0° eastern horizon, 90° overhead, 180° western horizon** |
       * | **`off`** | how far you are **from** it, toward the north |
       *
       * 🔴 **This is the whole difference between a ribbon and a ring.** The old
       * model asked *"what elevation is the band at this bearing?"*, and a line
       * at constant elevation seen from inside **is a circle centred on the
       * viewer** — no amount of narrowing or wobbling removes a circle from a
       * definition that is one.
       *
       * Here the curve lives entirely in this mapping: the texture holds a
       * straight stripe, and the sky bends it over the top.
       *
       * ⚠️ North is **−z** (`world.ts`), so `off` is measured from **−z**, and
       * `along` is measured in the east–up plane.
       */
      /**
       * 🔴 **Bearing across, elevation up — and the ARCH is baked into the
       * pixels.**
       *
       * ⚠️ **This was `along`/`off` for exactly one build.** Painting in the
       * ribbon's own frame put the curtain's width *across* the arch, which is
       * vertical at its apex and **horizontal at its legs** — so the rays fanned
       * out like a starburst. 🎯 haugur, within a minute: *"looks kinda like it
       * bends the wrong way."*
       *
       * 🔴 **Real aurora rays are vertical everywhere along the arc**, because
       * they follow a magnetic field that is near-vertical at this latitude. So
       * the arch keeps the great-circle geometry option C was built for, and the
       * rays run straight up from it.
       */
      const bearing = atan2(direction.x, direction.z.negate()).mul(RAD_TO_DEG);
      const elevation = asin(direction.y.clamp(-1, 1)).mul(RAD_TO_DEG);

      const u = bearing.div(AURORA_BEARING_SPAN).add(0.5);
      const v = float(1).sub(elevation.div(AURORA_ELEVATION_SPAN));

      const EDGE = 0.04;
      const insideU = smoothstepNode(0, EDGE, u).mul(smoothstepNode(1, 1 - EDGE, u));
      const insideV = smoothstepNode(0, 0.02, v).mul(smoothstepNode(1, 0.98, v));

      const sampled = texture(this.auroraTexture, vec2(u.add(this.auroraDrift), v));
      this.auroraMaterial.colorNode = sampled.rgb;
      /* 🔴 The painter puts the strength in ALPHA and the colour in RGB, and the
         blend is additive — light arriving, not a sticker over the sky. That is
         why stars stay visible through a weak display, as they do in the
         reference photographs. */
      this.auroraMaterial.opacityNode = sampled.a.mul(this.auroraOpacity).mul(insideU).mul(insideV);
    }
    this.auroraMaterial.transparent = true;
    this.auroraMaterial.depthWrite = false;
    /**
     * 🔴 **Depth-TESTED, unlike the dome — and this is both cheaper and more
     * correct.**
     *
     * It was `depthTest = false` with `renderOrder = -1`, which drew the curtain
     * across **every pixel of the screen** before the terrain painted over it.
     * Half of those pixels are usually ground. **The stars, the sun and the moon
     * have always been depth-tested** for exactly this reason: at 1,880 m they
     * sit inside the camera's 2,000 m far plane, so ordinary depth testing hides
     * them behind a hill with no special case.
     *
     * ⚠️ *The dome keeps `depthTest = false` because it is the background —
     * there is nothing behind it to be hidden by.*
     */
    this.auroraMaterial.depthTest = true;
    this.auroraMaterial.fog = false;
    this.auroraMaterial.toneMapped = false;
    this.auroraMaterial.blending = AdditiveBlending;
    this.auroraMaterial.side = BackSide;

    /* The upper hemisphere only: `thetaLength = PI/2` stops at the horizon.
       ⚠️ Slightly inside the dome, so it is never z-fighting with it. */
    this.aurora = new Mesh(
      new SphereGeometry(DOME_RADIUS - 20, 64, 32, 0, Math.PI * 2, 0, Math.PI / 2),
      this.auroraMaterial,
    );
    this.aurora.frustumCulled = false;
    /* ⚠️ No `renderOrder`: it draws with everything else so the depth buffer
       has terrain in it by the time the curtain is tested against it. Forcing it
       first was what made the depth test pointless. */
    this.aurora.visible = false;

    this.group.add(this.dome, this.stars, this.sun, this.moon, this.aurora);
    scene.add(this.group);

    /* The celestial pole: due north, tilted up by the latitude. ⚠️ North is −z
       (`world.ts`), so this leans toward −z — getting it wrong wheels the sky
       about a point in the southern sky. */
    const latR = (WORLD_LATITUDE * Math.PI) / 180;
    this.pole = new Vector3(0, Math.sin(latR), -Math.cos(latR)).normalize();
  }

  /**
   * Set the two sky stops.
   *
   * 🔴 **Uniforms, not a texture.** See the header: an equirect background
   * texture is converted to a cube map once and cached for ever, so writing
   * pixels into it after the first frame changes nothing on screen.
   */
  setGradient(zenith: string, horizon: string): void {
    this.zenith.value.set(zenith);
    this.horizon.value.set(horizon);
  }

  /**
   * 🌊 **Show or hide the whole sky — the dome, the sun, the moon, the stars
   * and the aurora together** (T61).
   *
   * 🔴 **Built because the sky is the one thing `scene.fog` cannot reach**, and
   * that is why the sea looked like nothing had happened. A magenta fog forced
   * to twenty metres turned every scrap of terrain magenta **and left the sky
   * bright blue** — and when you are standing on a seabed looking level, most
   * of your screen is sky.
   *
   * ⚠️ **Under water you should be looking at WATER above you**, not at
   * Iceland's sky through eight metres of sea.
   *
   * 🔴 **The caller must set `scene.background` when it hides this.** Nothing
   * is behind the dome: `scene.background` is never set in ordinary play, and
   * the header records that the sky rendered **black** the one time it was
   * relied on. **Hiding the sky without a background is a black sky, not a
   * wet one.**
   */
  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  /** @param origin the camera's position — the sky is drawn around the viewer */
  update(state: SkyObjectsState, origin: Vector3): void {
    this.group.position.copy(origin);

    /* 🌇 The sunset glow follows the sun round the sky, not just up and down. */
    this.sunTowards.value.set(state.sunDirection.x, state.sunDirection.y, state.sunDirection.z);
    this.glowColour.value.set(state.glowColour);
    this.glowStrength.value = clamp(state.sunsetGlow);

    const cloud = clamp(state.cloud);
    const fog = clamp(state.fog);
    /* Fog is opaque; cloud is not quite. */
    const throughFog = 1 - fog;

    // ── stars ──────────────────────────────────────────────────────────────
    /* Wheeled about the pole, once a day plus a sidereal nudge so the same stars
       are not overhead at the same hour every night. Nearly free, and it is what
       makes twenty minutes feel like a night passing. */
    this.spin.setFromAxisAngle(this.pole, state.days * 2 * Math.PI * (366 / 365));
    this.stars.quaternion.copy(this.spin);
    /* No cloud floor: you cannot see a star through cloud. */
    this.show(this.stars, this.starMaterial, state.night * (1 - cloud) * throughFog);

    // ── the sun ────────────────────────────────────────────────────────────
    this.place(this.sun, state.sunDirection);
    /* Eased across the horizon rather than switched — a sun that vanishes the
       instant it sets is not what a sunset looks like. */
    const sunUp = smoothstep(-2, 1, state.sunElevation);
    this.show(
      this.sun,
      this.sunMaterial,
      sunUp * throughFog * (SUN_THROUGH_CLOUD + (1 - SUN_THROUGH_CLOUD) * (1 - cloud)),
    );

    // ── the moon ───────────────────────────────────────────────────────────
    this.place(this.moon, state.moonDirection);
    const moonUp = smoothstep(-2, 1, state.moonElevation);
    /* ⚠️ A daytime moon is real and worth drawing, but faint — so it fades
       toward the day rather than switching off with the stars. */
    this.show(
      this.moon,
      this.moonMaterial,
      moonUp *
        throughFog *
        (MOON_THROUGH_CLOUD + (1 - MOON_THROUGH_CLOUD) * (1 - cloud)) *
        (0.3 + 0.7 * state.night),
    );

    // ── norðurljós ─────────────────────────────────────────────────────────
    this.updateAurora(state, cloud, fog);

    /* Rebuild the phase only when it has moved a step. */
    const step = Math.round(state.moonPhase * MOON_TEXTURE_STEPS) % MOON_TEXTURE_STEPS;
    if (step !== this.moonStep) {
      this.moonStep = step;
      writeMoonTexture(this.moonTexture.image.data as Uint8Array, step / MOON_TEXTURE_STEPS);
      this.moonTexture.needsUpdate = true;
    }
  }

  /**
   * 🌌 Repaint and reveal the curtain (T114).
   *
   * 🔴 **The sky does not decide how strong the aurora is — `@lofi/shared` does**,
   * from the same three ingredients the preview sheets used: **darkness, clear
   * sky and activity**. This method's whole job is *when to repaint* and *how
   * much to show*, which is the part that cannot be tested from here.
   *
   * ⚠️ **The cloud term is applied by `auroraLook`, not here.** The aurora is
   * about 100 km up, well above any weather, so overcast hides it completely —
   * it gets none of the 30% the sun keeps and the 15% the moon keeps (**D59**).
   * Fog is applied on top, being the one thing that is genuinely between you and
   * everything.
   */
  private updateAurora(state: SkyObjectsState, cloud: number, fog: number): void {
    const look = auroraLook({
      kp: state.kp,
      sunElevation: state.sunElevation,
      /* `auroraLook` speaks oktas; this class has already normalised to 0–1. */
      cloud: cloud * 8,
    });

    const strength = look.strength * (1 - fog);
    if (strength <= 0.002) {
      this.aurora.visible = false;
      /* ⚠️ Reset the clock, not the drift: coming back to a dark sky should not
         resume with a jump, but neither should the pattern restart from scratch
         every time a cloud passes. */
      this.auroraLastSeconds = state.realSeconds;
      return;
    }

    this.aurora.visible = true;
    this.auroraOpacity.value = strength;

    /* 🔴 The shape drifts on REAL seconds — haugur's call, and the reason is
       arithmetic: a game hour is 50 real seconds, so a curtain shaped by game
       time would move 72× too fast and read as a strobe. The STRENGTH above does
       follow game time, because that is what the archive changes. */
    const elapsed = Math.min(1, Math.max(0, state.realSeconds - this.auroraLastSeconds));
    this.auroraLastSeconds = state.realSeconds;
    this.auroraDrift.value += elapsed / Math.max(1, look.driftSeconds);

    /**
     * 🔴 **Repaint only when Kp changes**, which is once every three game hours
     * — about two and a half real minutes.
     *
     * 🔴 **⚠️ READ THIS BEFORE BELIEVING THE FRAME-RATE STORY THAT WAS HERE.**
     *
     * This comment used to say the repaint cost haugur 57 frames per second. **It
     * did not, and the reasoning was a textbook case of fitting a cause to a
     * number.** He reported 3–4 FPS, the repaint was the newest thing in the
     * frame loop, and it was blamed twice — first by shrinking the texture, then
     * by removing the repaint altogether — with neither change measured.
     *
     * ✅ **He then measured it himself, and the answer was the machine.** On a
     * Windows VM **with no GPU**: 3 FPS with the aurora and **2–3 without it**. On
     * a laptop with an Intel Arc 140V: **60 FPS with, 70–110 without.**
     *
     * **So the aurora costs real frames on real hardware — but it never cost 57,
     * and the VM's 3 FPS had nothing to do with it.**
     *
     * ✅ **Painting once per Kp change is still right**, for a reason that does
     * not depend on the wrong diagnosis: a `DataTexture` marked `needsUpdate` is
     * re-sent to the GPU, and there was never anything to gain by doing it in the
     * frame loop.
     *
     * ✅ **Nothing about the picture needed to be repainted anyway.** Every value
     * baked into the texture — the band's height, its colours, its rays — depends
     * only on **Kp**. Brightness comes from a uniform and motion comes from the
     * offset above, and neither touches the pixels.
     */
    const kpStep = Math.round(state.kp * 4) / 4;
    if (this.auroraPaintedKp === kpStep) return;
    this.auroraPaintedKp = kpStep;

    const painted = paintAuroraTexture(look, {
      width: AURORA_TEXTURE_WIDTH,
      height: AURORA_TEXTURE_HEIGHT,
      /* The pattern's own phase is fixed; the drift above does the moving. */
      phase: 0,
      /* ⚠️ The strength is carried by the uniform above, so the texture is
         painted at full value and dimmed once. Multiplying it in twice was the
         first version, and it made a Kp 2 night invisible. */
      valueScale: PHOTOGRAPH_VALUE_SCALE,
    });
    this.auroraTexture.image.data.set(painted);
    this.auroraTexture.needsUpdate = true;
  }

  private place(object: Sprite, direction: { x: number; y: number; z: number }): void {
    object.position.set(
      direction.x * SKY_RADIUS,
      direction.y * SKY_RADIUS,
      direction.z * SKY_RADIUS,
    );
  }

  /**
   * ⚠️ **`visible` is the OBJECT's, `opacity` is the MATERIAL's.** Setting
   * `material.visible` compiles — `Material` has the field — and does nothing
   * useful, which would leave a fully transparent sun being drawn all day.
   */
  private show(object: Mesh | Sprite, material: { opacity: number }, value: number): void {
    const clamped = clamp(value);
    material.opacity = clamped;
    object.visible = clamped > 0.004;
  }
}

/**
 * One quad per star, tangent to the dome.
 *
 * 🔴 **The trick that makes this simple: the camera is at the centre of the
 * sphere, so a quad tangent to the sphere is always square-on to the camera.**
 * No billboarding, no per-frame work, no `Points` size path to depend on.
 */
function starGeometry(): BufferGeometry {
  const { positions, brightness } = starField(STAR_COUNT);

  const vertices = new Float32Array(STAR_COUNT * 4 * 3);
  const colours = new Float32Array(STAR_COUNT * 4 * 3);
  /* UVs, so the soft round dot can be mapped onto each quad. Without them a
     star is a square, which is what confetti is. */
  const uvs = new Float32Array(STAR_COUNT * 4 * 2);
  const indices = new Uint32Array(STAR_COUNT * 6);

  const dir = new Vector3();
  const right = new Vector3();
  const up = new Vector3();
  const corner = new Vector3();
  /* Any vector not parallel to `dir` will do to build a basis from. Using two
     candidates avoids the degenerate case straight up, where the star sits at
     the pole and the cross product collapses to nothing. */
  const helperA = new Vector3(0, 1, 0);
  const helperB = new Vector3(1, 0, 0);

  for (let i = 0; i < STAR_COUNT; i++) {
    /* eslint-disable security/detect-object-injection --
       Every index is a loop counter against arrays sized from STAR_COUNT. */
    dir.set(positions[i * 3] ?? 0, positions[i * 3 + 1] ?? 0, positions[i * 3 + 2] ?? 0);
    const helper = Math.abs(dir.y) > 0.95 ? helperB : helperA;
    right.crossVectors(helper, dir).normalize();
    up.crossVectors(dir, right).normalize();

    const b = brightness[i] ?? 1;
    /* 🔴 Brighter stars are drawn bigger, which is how the eye reads magnitude.
       A field of identical dots is noise whatever size they are. */
    const half =
      worldSize(
        STAR_DIAMETER_FAINT + (STAR_DIAMETER_BRIGHT - STAR_DIAMETER_FAINT) * b,
        SKY_RADIUS,
      ) / 2;
    for (let c = 0; c < 4; c++) {
      const sx = c === 0 || c === 3 ? -half : half;
      const sy = c < 2 ? -half : half;
      corner
        .copy(dir)
        .multiplyScalar(SKY_RADIUS)
        .addScaledVector(right, sx)
        .addScaledVector(up, sy);

      const v = (i * 4 + c) * 3;
      vertices[v] = corner.x;
      vertices[v + 1] = corner.y;
      vertices[v + 2] = corner.z;
      /* A touch bluer than white — the night sky's own hue, four stops down
         (V20), rather than the pure white that reads as a dead pixel. */
      colours[v] = b * 0.94;
      colours[v + 1] = b * 0.94;
      colours[v + 2] = b;

      const u = (i * 4 + c) * 2;
      uvs[u] = sx < 0 ? 0 : 1;
      uvs[u + 1] = sy < 0 ? 0 : 1;
    }

    const base = i * 4;
    const t = i * 6;
    indices[t] = base;
    indices[t + 1] = base + 1;
    indices[t + 2] = base + 2;
    indices[t + 3] = base;
    indices[t + 4] = base + 2;
    indices[t + 5] = base + 3;
    /* eslint-enable security/detect-object-injection */
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(vertices, 3));
  geometry.setAttribute('color', new BufferAttribute(colours, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(new BufferAttribute(indices, 1));
  return geometry;
}

const clamp = (v: number): number => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}
